import React from 'react';
import { Shield, ChevronRight } from 'lucide-react';

const GOLD = '#C9A961';
const ORANGE = '#FF5E00';
const BG = '#050508';

const Footer = () => {
  const applyHref = process.env.REACT_APP_ENROLL_URL || '/academy/apply';
  const isExternalApply = /^https?:\/\//i.test(applyHref);
  const applyTargetProps = isExternalApply ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  return (
    <footer
      className="relative text-white overflow-hidden"
      style={{ backgroundColor: BG }}
      data-testid="site-footer"
    >
      {/* subtle grid texture — matches Academy hero */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(201,169,97,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.06) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      {/* top gold hairline */}
      <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 pt-20 pb-10">
        {/* Mission block */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 border border-white/10 backdrop-blur-xl px-4 py-2 mb-6">
            <Shield className="w-3.5 h-3.5" style={{ color: GOLD }} />
            <span className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: GOLD }}>The Sovereign Academy</span>
          </div>
          <p className="text-2xl md:text-3xl font-black uppercase leading-[1.05] tracking-[-0.02em]">
            Strengthen the athlete. <br className="hidden md:block" />
            Sharpen the scholar. <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text" style={{ WebkitBackgroundClip: 'text', backgroundImage: `linear-gradient(135deg, ${ORANGE}, ${GOLD})` }}>
              Remove the hurdles.
            </span>
          </p>
        </div>

        {/* Link grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-14">
          <FooterColumn title="Academy">
            <FooterLink href="/academy">Scholar Athlete Academy</FooterLink>
            <FooterLink href="/academy?type=parent">Scholar Parent Academy</FooterLink>
            <FooterLink href="/academy/lchaim">L&apos;Chaim: The 13th Year</FooterLink>
            <FooterLink href="/academy/electives">Elective Courses</FooterLink>
            <FooterLink href="/academy/combine">The Combine</FooterLink>
          </FooterColumn>

          <FooterColumn title="Enroll">
            <FooterLink href={applyHref} {...applyTargetProps}>Apply to the Academy</FooterLink>
            <FooterLink href="/academy/enroll">Free Signup</FooterLink>
            <FooterLink href="/academy/login">Member Login</FooterLink>
            <FooterLink href="/academy/family">Family Hub</FooterLink>
            <FooterLink href="/academy/welcome">Parent Welcome</FooterLink>
          </FooterColumn>

          <FooterColumn title="About">
            <FooterLink href="/academy/about">Our Story</FooterLink>
            <FooterLink href="/academy">The Sovereign Path</FooterLink>
            <FooterLink href="/academy/reclassification-101">Reclassification 101</FooterLink>
          </FooterColumn>

          <FooterColumn title="Contact">
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-gray-400 leading-relaxed mb-1.5">
              <span className="text-gray-600">Email · </span>
              <a href="mailto:info@prsvr.com" className="hover:text-white transition-colors">info@prsvr.com</a>
            </p>
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-gray-400 leading-relaxed mb-1.5">
              <span className="text-gray-600">Text · </span>
              <a href="sms:6613831744" className="hover:text-white transition-colors">661.383.1744</a>
            </p>
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-gray-500 leading-relaxed">
              <span className="text-gray-600">Base · </span>Los Angeles, CA
            </p>
            <div className="mt-5 flex gap-3">
              <SocialDot href="https://instagram.com/prsvr" label="IG" />
              <SocialDot href="https://www.youtube.com/@theprsvrfamily" label="YT" />
              <SocialDot href="https://www.linkedin.com/in/prsvr" label="LI" />
            </div>
          </FooterColumn>
        </div>

        {/* Apply strip */}
        <div
          className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-6 md:p-8 mb-10"
          style={{ backgroundColor: '#0A0A0E', border: `1px solid ${GOLD}25` }}
          data-testid="footer-apply-strip"
        >
          <div>
            <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-2" style={{ color: GOLD }}>Enrollment Now Open</div>
            <div className="text-xl md:text-2xl font-black uppercase tracking-tight">Ready to claim your seat?</div>
            <div className="text-[11px] text-gray-500 mt-1 tracking-wide">Cohorts are capped. Applications reviewed in the order received.</div>
          </div>
          <a
            href={applyHref}
            {...applyTargetProps}
            className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-black px-6 py-3.5 transition-all hover:-translate-y-0.5 flex-shrink-0"
            style={{ backgroundColor: ORANGE, boxShadow: `0 0 0 1px ${ORANGE}, 0 10px 24px -10px ${ORANGE}90` }}
            data-testid="footer-apply-cta"
          >
            Apply Now
            <ChevronRight className="h-4 w-4" />
          </a>
        </div>

        {/* Bottom bar */}
        <div className="pt-8" style={{ borderTop: `1px solid rgba(255,255,255,0.06)` }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src="/logos/saa-athlete-mark.png" alt="Scholar Athlete Academy" className="h-8 w-auto" />
              <div className="flex flex-col leading-none">
                <span className="text-[9px] font-black tracking-[0.28em] uppercase text-white">Scholar Athlete</span>
                <span className="text-[9px] font-black tracking-[0.28em] uppercase" style={{ color: GOLD }}>Academy</span>
              </div>
            </div>
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-gray-600">
              © 2026 Scholar Athlete Academy · All Rights Reserved
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-bold tracking-[0.15em] uppercase text-gray-500">
              <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy</a>
              <span className="text-gray-800">·</span>
              <a href="/terms-of-service" className="hover:text-white transition-colors">Terms</a>
              <span className="text-gray-800">·</span>
              <a href="/refund-policy" className="hover:text-white transition-colors">Refund</a>
              <span className="text-gray-800">·</span>
              <a href="/pricing-policy" className="hover:text-white transition-colors">Pricing</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterColumn = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-4" style={{ color: GOLD }}>
      {title}
    </div>
    <ul className="flex flex-col gap-2.5">{children}</ul>
  </div>
);

const FooterLink = ({ href, children, ...rest }) => (
  <li>
    <a
      href={href}
      className="text-[11px] font-bold tracking-[0.12em] uppercase text-gray-400 hover:text-white transition-colors"
      {...rest}
    >
      {children}
    </a>
  </li>
);

const SocialDot = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center justify-center h-8 w-8 border border-white/15 text-[9px] font-black tracking-[0.15em] uppercase text-gray-400 hover:text-white hover:border-white/40 transition-colors"
    aria-label={label}
  >
    {label}
  </a>
);

export default Footer;
